import PropTypes from 'prop-types';
import {
  Children, useEffect, useMemo, useState,
} from 'react';
import { getCmsPage } from 'modules/cms/actions';
import { useDispatch, useSelector } from 'react-redux';
import useCmsPreview from 'hooks/useCmsPreview';
import { CMS_MESSAGES } from 'constants/cms';
import { motion } from 'framer-motion';
import BlockRenderer from './BlockRenderer';

/**
 * CmsBlocksV2 — consumidor responsive del CMS legacy.
 *
 * Reusa el slice `cms` y el endpoint `/cms/pages/url` existentes (sin
 * cambios en backend). Solo cambia el renderer: cada bloque pasa por
 * BlockRenderer v2 que aplica responsive Tailwind y AppShell wraps.
 *
 * API idéntica al <CmsBlocks /> legacy para drop-in replacement.
 */
const CmsBlocksV2 = (props) => {
  const {
    children = null,
    animate = null,
    renderOnError = null,
    renderOnEmpty = null,
    blockProps = {},
    renderCmsItem = null,
  } = props;

  const [previewBlocks, setPreviewBlocks] = useState(null);
  const dispatch = useDispatch();
  const path = document.location.pathname.replace(/\//g, '-').replace(/^-/g, '');
  const { pages: { data: pages } } = useSelector((state) => state.cms);
  const { value: previewMessage, isPreview } = useCmsPreview(CMS_MESSAGES.UPDATE_BLOCKS);
  const numChilds = useMemo(
    () => (children ? Children.toArray(children).filter(Boolean).length : 0),
    [children],
  );
  const page = pages[path] || null;
  const isReady = page?.loaded;

  useEffect(() => {
    if (!pages[path]) {
      dispatch(getCmsPage(path));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Array.isArray(previewMessage)) {
      setPreviewBlocks(previewMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewMessage]);

  if (page?.error) {
    return renderOnError;
  }

  if (!page || page?.loading) {
    return null;
  }

  if (isReady && !page?.blocks?.length && !previewBlocks?.length) {
    return renderOnEmpty;
  }

  return (
    <>
      {isReady && Children.map(children, (child, i) => (
        <motion.div
          key={`block-${i}`}
          {...(animate || {})}
          custom={i}
        >
          {child}
        </motion.div>
      ))}
      {(previewBlocks || page?.blocks || []).map((block, i) => {
        const key = block.id || i;
        const auxProps = {
          type: block.type,
          value: block.value,
          blockProps,
          isPreview,
          renderCmsItem,
        };
        if (!animate) {
          return (<BlockRenderer key={key} {...auxProps} />);
        }
        return (
          <motion.div
            key={`block-${i + numChilds}`}
            {...(animate || {})}
            custom={i + numChilds}
          >
            <BlockRenderer key={key} {...auxProps} />
          </motion.div>
        );
      })}
    </>
  );
};

CmsBlocksV2.propTypes = {
  animate: PropTypes.object,
  children: PropTypes.node,
  renderOnError: PropTypes.node,
  renderOnEmpty: PropTypes.node,
  blockProps: PropTypes.object,
  renderCmsItem: PropTypes.func,
};

export default CmsBlocksV2;
